// Les téléphones par QR code, côté tablette, pour tous les jeux (D-167 ; maquette T1). « Rejoindre avec un téléphone » :
// un QR par équipe (sans compte, valable le temps de la partie) et un pour le coach (son compte, sa télécommande) ; qui est
// relié ; « Jouer sans téléphones » ; « Coup d'envoi · 3, 2, 1 ». Pendant la partie : les gestes des téléphones arrivent
// dans l'ordre de la base (LiveHost), la tablette les applique avec le moteur du jeu et publie la partie ; « Téléphones »
// en haut rouvre les QR ; « Arbitré par Marc » à chaque décision du téléphone du coach. D-174 : l'appareil de la partie
// peut être le téléphone du coach (une partie sans tablette) : les QR s'y affichent aussi, l'un sous l'autre ; les
// téléphones des équipes restent un plus, jamais une condition.
import './game2.css';
import { LiveHost, type LiveAction, type SeatState } from '../lib/game-live-api';
import type { LiveKind, LiveView } from '../domain/game-live';
import { esc, inSentence, jersey, qrSvg, type Kit } from './game-art';

export const ICON = {
  phone: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="7" y="2.5" width="10" height="19" rx="2.4"/><path d="M11 18.5h2"/></svg>`,
  lock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>`,
  unlock: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 7.5-2"/></svg>`,
  shield: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6z"/><path d="M9 12l2 2 4-4"/></svg>`,
  stop: `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor"/></svg>`,
  swap: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 8h13l-3-3M20 16H7l3 3"/></svg>`,
  qr: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 18h2v2h-2zM14 18h2M18 14h2"/></svg>`,
  eye: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>`,
  hand: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 13V5.5a1.5 1.5 0 0 1 3 0V11M11 10V4.5a1.5 1.5 0 0 1 3 0V11M14 10.5V6a1.5 1.5 0 0 1 3 0v7.5a6.5 6.5 0 0 1-6.5 6.5H10a5 5 0 0 1-4-2l-2.6-3.6a1.5 1.5 0 0 1 2.3-1.9L8 15"/></svg>`,
  wifioff: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 8.5a15 15 0 0 1 6-3.2M22 8.5a15 15 0 0 0-9.5-3.9M5 12a10 10 0 0 1 3.5-2.2M19 12a10 10 0 0 0-3-2M8.5 15.5a5 5 0 0 1 7 0M12 19h.01M3 3l18 18"/></svg>`,
};

/** La mise en page compacte des jeux (un téléphone, une tablette en portrait) : la même règle que game.css et game2.css. */
export const COMPACT = '(max-width: 900px), (max-aspect-ratio: 1/1)';
/** Un téléphone d'équipe est « relié » s'il a donné signe de vie depuis 15 s. */
export const SEEN_MS = 15_000;
export interface LiveTeamSeat { seat: string; bib: string; kit: Kit; name: string }
export interface LiveCtl {
  readonly host: LiveHost;
  /** L'heure de la base, estimée sur la tablette (le temps de la partie). */
  now(): number;
  /** Le téléphone d'une équipe : relié, hors réseau, ou aucun. */
  phoneOf(seat: string): 'on' | 'off' | null;
  /** Combien de téléphones sont reliés (équipes et coach). */
  online(): number;
  coachName(): string | null;
  publish(view: LiveView, status: 'lobby' | 'running' | 'over'): void;
  /** Rafraîchir l'écran « Rejoindre » s'il est ouvert. */
  close(): Promise<void>;
}

/** L'adresse ouverte par le QR d'une équipe ; celle de la télécommande du coach (dans l'app, avec son compte). */
export const teamLink = (token: string) => `${location.origin}/j/${token}`;
export const coachLink = (liveId: string) => `${location.origin}${location.pathname.replace(/\/+$/, '') || ''}#/basket/jeu/coach/${liveId}`;

/**
 * Ouvre la partie aux téléphones. `onAction` reçoit chaque geste une fois, dans l'ordre de la base ; `onSeats` dit qui est
 * relié (pour l'écran). Rend null si la base refuse (hors réseau) : on joue alors sur la tablette.
 */
export async function openLive(o: {
  teamId: string; gameId: string | null; kind: LiveKind; title: string; teams: LiveTeamSeat[];
  onAction: (a: LiveAction) => void; onSeats?: (s: SeatState[]) => void; onToast?: (text: string) => void;
}): Promise<LiveCtl | null> {
  let prev = new Map<string, number | null>();
  let host: LiveHost;
  try {
    host = await LiveHost.open({
      teamId: o.teamId, gameId: o.gameId, kind: o.kind, title: o.title, bibs: o.teams.map(t => t.bib),
      onActions: acts => { for (const a of acts) o.onAction(a); },
      onSeats: seats => {
        // « Prendre la main » : un autre téléphone a pris la place d'une équipe ; la tablette le dit.
        for (const s of seats) {
          const before = prev.get(s.seat);
          if (before && s.since && s.since !== before && s.seat !== 'coach') {
            const t = o.teams.find(x => x.seat === s.seat);
            if (t) o.onToast?.(`${t.name} : un autre téléphone a pris la main`);
          }
        }
        prev = new Map(seats.map(s => [s.seat, s.since]));
        o.onSeats?.(seats);
      },
    });
  } catch { return null; }
  const fresh = (s: SeatState | undefined) => !!s?.held && !!s.seen && host.clock.now() - s.seen < SEEN_MS;
  return {
    host,
    now: () => host.clock.now(),
    phoneOf: seat => { const s = host.seats.find(x => x.seat === seat); return !s?.held ? null : fresh(s) ? 'on' : 'off'; },
    online: () => host.seats.filter(fresh).length,
    coachName: () => host.seats.find(s => s.seat === 'coach' && s.held)?.name ?? null,
    publish: (view, status) => host.publish(view, status),
    close: () => host.close(),
  };
}

/**
 * L'écran « Rejoindre avec un téléphone » (T1), dans `box` (le plein écran de la tablette). `reopen` : pendant la partie
 * (« Téléphones » en haut), avec « Revenir à la partie ». Rend une fonction qui le redessine (les places changent).
 */
export function drawJoin(box: HTMLElement, live: LiveCtl, o: { teams: LiveTeamSeat[]; reopen?: boolean; reserve?: string; phones?: boolean; remote?: boolean }): () => void {
  const draw = () => {
    const showTeams = o.phones !== false, showCoach = o.remote !== false;
    const n = showTeams ? o.teams.length : 0;
    const coach = live.coachName();
    const on = live.online();
    const card = (t: LiveTeamSeat) => {
      const st = live.phoneOf(t.seat);
      const token = live.host.places.find(p => p.seat === t.seat)?.token ?? '';
      return `<div class="sl-qc ${st === 'on' ? 'on' : ''}" style="--c:${t.kit.color};--ci:${t.kit.ink}">
        <div class="band">${jersey(t.kit, 36)}<span><b>${esc(t.name)}</b><small>scannez votre couleur</small></span></div>
        <div class="qr">${qrSvg(teamLink(token), `QR des ${t.name.replace(/^Les /, '')}`)}</div>
        <span class="stt ${st === 'on' ? '' : 'wait'}"><i class="dot"></i>${st === 'on' ? 'Relié · 1 téléphone' : st === 'off' ? 'Hors réseau' : 'En attente…'}</span>
        <p class="sub">${st === 'on' ? `Posez-le au plot ${esc(t.name.startsWith('Les ') ? `des ${t.name.slice(4)}` : `de ${t.name}`)}.`
          : `Sans téléphone, ${esc(inSentence(t.name))} jouent ici, sur cet écran.`}</p></div>`;
    };
    box.innerHTML = `<div class="sl-join" data-guide="gm-join">
      <div class="sl-join-h"><div><h2>Rejoindre avec un téléphone</h2>
        <p>En option, un téléphone par équipe : ouvrez l’appareil photo et visez <b>votre couleur</b>. La page de l’équipe s’ouvre : sa carte, ${o.reserve ? 'son sable, son STOP' : 'son pion, « Fait ! »'}. <b>Sans compte, sans appli</b>, valable le temps de la partie. Sans téléphone, tout se touche sur cet écran.</p></div>
        <div class="cnt"><b>${on} / ${n + (showCoach ? 1 : 0)}</b>reliés</div></div>
      <div class="sl-qrs" style="--n:${n}">${showTeams ? o.teams.map(card).join('') : ''}
        ${showCoach ? '' : '<!--'}<div class="sl-qc coach ${coach ? 'on' : ''}" style="--c:#0F4C81;--ci:#FFFFFF"><div class="band">${ICON.lock.replace('<svg ', '<svg width="26" height="26" ')}<span><b>Coach</b><small>avec ton compte</small></span></div>
          <div class="qr">${qrSvg(coachLink(live.host.id), 'QR du coach')}</div><span class="stt ${coach ? '' : 'wait'}"><i class="dot"></i>${coach ? `${esc(coach)} · relié` : 'En attente…'}</span>
          <p class="sub">Ta télécommande : STOP, Raté et l’arbitrage de chaque équipe, la pause. Ton compte suffit.</p></div>${showCoach ? '' : '-->'}
      </div>
      <div class="sl-join-f"><span class="ico">${ICON.shield}</span>
        <p><b>Le QR ne collecte rien</b> : ni nom, ni compte, ni appli ; rien n’est gardé sur le téléphone. Il s’éteint à la fin de la partie. ${o.reopen ? 'Un téléphone perdu : on rescanne son QR.' : 'Les QR se referment au coup d’envoi ; « Téléphones » en haut les rouvre.'}</p>
        ${o.reopen ? `<span></span><button type="button" class="gm-btn go" data-join-back>Revenir à la partie</button>`
          : `<button type="button" class="gm-btn no" data-join-none>Jouer sans téléphones</button><button type="button" class="gm-btn go" data-join-go>▶ Coup d’envoi · 3, 2, 1</button>`}</div>
    </div>`;
  };
  draw();
  return draw;
}

/** Le décompte du coup d'envoi (3, 2, 1) par-dessus le plein écran. */
export async function countdown(host: HTMLElement, reduce: boolean): Promise<void> {
  const el = document.createElement('div');
  el.className = 'sl-count';
  host.append(el);
  for (const k of ['3', '2', '1']) { el.textContent = k; await new Promise(r => setTimeout(r, reduce ? 350 : 800)); }
  el.remove();
}

/** Une ligne en haut de la tablette, quelques secondes (« Arbitré par Marc »). */
export function flash(host: HTMLElement, text: string, ms = 2600): void {
  host.querySelector('.sl-toast')?.remove();
  const el = document.createElement('div');
  el.className = 'sl-toast';
  el.setAttribute('role', 'status');
  el.textContent = text;
  host.append(el);
  setTimeout(() => el.remove(), ms);
}
